import {
  EGlobalSearchItemType,
  EGlobalSearchTargetKind,
  type IGlobalSearchStaticEntry,
} from "../types/global-search";
import {
  EGlobalSearchGate,
  type IBuildGlobalSearchOrganizationTabEntryProps,
  type IBuildGlobalSearchPageEntryProps,
  type IBuildGlobalSearchProfileTabEntryProps,
  type IBuildGlobalSearchSettingOptionEntryProps,
} from "../types/global-search-index";
import { SETTINGS_SECTION_VALUES } from "./navigation-tabs";

const KEYWORD_NAMESPACE = "globalSearchIndex";
const PROFILE_SECTION_TITLE_KEY = "settings:sections.profile";
const ORGANIZATION_SECTION_TITLE_KEY = "settings:sections.organization";

// NOTE: Synonyms live in the globalSearchIndex namespace under a key derived
// from the entry id, so an entry and its synonyms can never be paired wrongly:
// "page.analytics.probes" → "globalSearchIndex:keywords.page.analytics_probes".
// The first id segment stays a JSON level; the rest is joined with "_" because
// "page.analytics" is an entry of its own and a JSON key cannot be both a
// string and an object.
export const getGlobalSearchKeywordKey = (id: string): string => {
  const [group = "", ...rest] = id.split(".");
  return `${KEYWORD_NAMESPACE}:keywords.${group}.${rest.join("_")}`;
};

export const buildGlobalSearchPageEntry = ({
  id,
  titleKey,
  subtitleKey,
  gates,
  target,
}: IBuildGlobalSearchPageEntryProps): IGlobalSearchStaticEntry => ({
  id,
  type: EGlobalSearchItemType.PAGE,
  titleKey,
  ...(subtitleKey === undefined ? {} : { subtitleKey }),
  keywordKeys: [getGlobalSearchKeywordKey(id)],
  gates: [...gates],
  target,
});

export const buildGlobalSearchProfileTabEntry = ({
  tab,
  titleKey,
  gates,
}: IBuildGlobalSearchProfileTabEntryProps): IGlobalSearchStaticEntry => {
  const id = `settings.${SETTINGS_SECTION_VALUES.PROFILE}.${tab}`;

  return {
    id,
    type: EGlobalSearchItemType.SETTINGS_TAB,
    titleKey,
    subtitleKey: PROFILE_SECTION_TITLE_KEY,
    keywordKeys: [getGlobalSearchKeywordKey(id)],
    gates: [...gates],
    target: {
      kind: EGlobalSearchTargetKind.SETTINGS,
      params: { section: SETTINGS_SECTION_VALUES.PROFILE, tab },
    },
  };
};

// NOTE: IN_ORGANIZATION is added for every organization tab: the target carries
// no organizationUuid (the platform fills in the active project's
// organization), which only means something inside an organization project.
export const buildGlobalSearchOrganizationTabEntry = ({
  tab,
  titleKey,
  gates,
}: IBuildGlobalSearchOrganizationTabEntryProps): IGlobalSearchStaticEntry => {
  const id = `settings.${SETTINGS_SECTION_VALUES.ORGANIZATION}.${tab}`;

  return {
    id,
    type: EGlobalSearchItemType.SETTINGS_TAB,
    titleKey,
    subtitleKey: ORGANIZATION_SECTION_TITLE_KEY,
    keywordKeys: [getGlobalSearchKeywordKey(id)],
    gates: [EGlobalSearchGate.IN_ORGANIZATION, ...gates],
    target: {
      kind: EGlobalSearchTargetKind.SETTINGS,
      params: { section: SETTINGS_SECTION_VALUES.ORGANIZATION, tab },
    },
  };
};

// NOTE: NSAI-6595 — one control inside a settings tab. The target is the tab's
// own target plus `setting`, the GLOBAL_SEARCH_SETTING_IDS value the platform
// scrolls to and highlights once the tab is open. The tab's own subtitle (its
// settings area) becomes the option's section.
export const buildGlobalSearchSettingOptionEntry = ({
  tabEntry,
  setting,
  titleKey,
  gates = [],
}: IBuildGlobalSearchSettingOptionEntryProps): IGlobalSearchStaticEntry => {
  const id = `setting.${tabEntry.target.params.tab ?? ""}.${setting}`;

  return {
    id,
    type: EGlobalSearchItemType.SETTINGS_OPTION,
    titleKey,
    subtitleKey: tabEntry.titleKey,
    ...(tabEntry.subtitleKey === undefined
      ? {}
      : { sectionKey: tabEntry.subtitleKey }),
    keywordKeys: [getGlobalSearchKeywordKey(id)],
    gates: [...new Set([...tabEntry.gates, ...gates])],
    target: {
      kind: tabEntry.target.kind,
      params: { ...tabEntry.target.params, setting },
    },
  };
};
